import axios from 'helpers/api';

/**
 * Service module for user self-registration and the admin approval queue
 * (userregistrations.md §5 / U1.4). Follows the helpers/sharedLivingApi.js
 * precedent: endpoint paths and response unwrapping live here so screens stay
 * focused on UI.
 *
 * Two kinds of calls:
 *  - fetch / list calls THROW on failure (HTTP error or `status: false`) so a
 *    screen can show an error state with a retry.
 *  - action calls (register, approve, reject, set password) RESOLVE to
 *    `{ ok, message, ...responseBody }` — also for 4xx responses that carry a
 *    message — so forms can show the server's exact words inline (e.g. the
 *    privilege-escalation error on approve, which must never be swallowed).
 *    Only a network failure (no response at all) throws.
 */

const baseUrl = () => process.env.REACT_APP_SERVER_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`
});

const isTruthyFlag = value =>
  value === true || value === 1 || value === '1' || value === 'true';

const firstArray = (...candidates) => candidates.find(Array.isArray) || [];

const isPlainObject = value =>
  !!value && typeof value === 'object' && !Array.isArray(value);

export const NETWORK_ERROR_MESSAGE =
  "We couldn't reach MapYourMeds. Check your internet connection and try again.";

const messageError = message => {
  const error = new Error(message);
  error.isApiMessage = true;
  return error;
};

/**
 * Best human-readable message for a failed call: the server's own message when
 * it sent one, a connection hint when nothing came back, else the fallback.
 */
export const getApiErrorMessage = (
  error,
  fallback = 'Something went wrong. Please try again.'
) => {
  const body = error?.response?.data;
  if (isPlainObject(body) && (body.message || body.error)) {
    return String(body.message || body.error);
  }
  if (error?.isApiMessage && error.message) {
    return error.message;
  }
  if (error && !error.response && error.message === 'Network Error') {
    return NETWORK_ERROR_MESSAGE;
  }
  return fallback;
};

const ensureOk = (data, fallback) => {
  if (isPlainObject(data) && 'status' in data && !isTruthyFlag(data.status)) {
    throw messageError(data.message || fallback);
  }
  return data;
};

const runAction = async (request, fallback) => {
  try {
    const { data } = await request();
    const body = isPlainObject(data) ? data : {};
    const ok = isTruthyFlag(body.status) || isTruthyFlag(body.success);
    return { ...body, ok, message: body.message || (ok ? '' : fallback) };
  } catch (error) {
    if (!error?.response) {
      throw error;
    }
    const body = isPlainObject(error.response.data) ? error.response.data : {};
    return {
      ...body,
      ok: false,
      message: getApiErrorMessage(error, fallback),
      httpStatus: error.response.status
    };
  }
};

const byName = (a, b) => a.name.localeCompare(b.name);

export const normalizeGroup = group => ({
  ...group,
  id: group?.id ?? group?.group_id ?? group?.value,
  name: String(group?.name ?? group?.group_name ?? group?.key ?? '').trim()
});

export const normalizeFacility = facility => ({
  ...facility,
  id: facility?.id ?? facility?.facility_id ?? facility?.value,
  name: String(
    facility?.name ?? facility?.facility_name ?? facility?.key ?? ''
  ).trim()
});

/* ------------------------------------------------------------------------ */
/*                       Public (no login) — applicant                      */
/* ------------------------------------------------------------------------ */

/**
 * GET /users/registration-groups (R2.2)
 * Only groups with allow_self_registration = 1. The list may legitimately be
 * EMPTY — the form renders a "not open" state instead of an empty dropdown.
 */
export const fetchRegistrationGroups = async () => {
  const { data } = await axios.get(`${baseUrl()}/users/registration-groups`);
  ensureOk(data, 'Unable to load organizations');
  return firstArray(data?.data, data?.groups, data)
    .map(normalizeGroup)
    .filter(group => group.id !== undefined && group.id !== null)
    .sort(byName);
};

/**
 * GET /users/registration-facilities/:groupId (R2.3)
 */
export const fetchRegistrationFacilities = async groupId => {
  const { data } = await axios.get(
    `${baseUrl()}/users/registration-facilities/${encodeURIComponent(groupId)}`
  );
  ensureOk(data, 'Unable to load facilities');
  return firstArray(data?.data, data?.facilities, data)
    .map(normalizeFacility)
    .filter(facility => facility.id !== undefined && facility.id !== null)
    .sort(byName);
};

/**
 * POST /users/registration-domain-check { email }
 * → { status: true, needs_organization: boolean }
 * Resolves to `needs_organization`: true when the email's domain isn't one of
 * our clients, so the applicant has to type their organization's name. Throws
 * on a network / server error, `status: false`, or an answer without the flag,
 * so the caller can fall back to asking. Nothing from the answer is shown.
 */
export const checkRegistrationDomain = async email => {
  const { data } = await axios.post(
    `${baseUrl()}/users/registration-domain-check`,
    { email }
  );
  ensureOk(data, 'Unable to check this email');
  const flag = isPlainObject(data) ? data.needs_organization : undefined;
  if (flag === undefined || flag === null) {
    throw messageError('Unable to check this email');
  }
  return isTruthyFlag(flag);
};

/**
 * POST /users/register (R2.4)
 * { first_name, last_name, email, phone, address, address2, city, state, zip,
 *   organization_name?, website } — organization_name only when the email's
 *   domain isn't a known client (see checkRegistrationDomain).
 * `website` is the honeypot — always '' for a human.
 * The server answers with the SAME generic success whether or not the email
 * already exists (R2.5) — callers must not branch on anything else.
 */
export const submitRegistration = payload =>
  runAction(
    () => axios.post(`${baseUrl()}/users/register`, payload),
    'We could not submit your request. Please try again.'
  );

/**
 * POST /users/set-invite-password (R4.6)
 * { hash, password, confirm_password } — sent exactly like
 * PasswordResetForm.js sends /users/reset-password (the server stores what
 * the client sends, so this must not diverge — K2).
 */
export const setInvitePassword = ({ hash, password, confirmPassword }) =>
  runAction(
    () =>
      axios.post(`${baseUrl()}/users/set-invite-password`, {
        hash,
        password,
        confirm_password: confirmPassword
      }),
    'We could not set your password. Please try again.'
  );

/**
 * POST /users/check-username — already public, used by AddFacilityStaff.
 * Resolves true when the username is free.
 */
export const checkUsernameAvailability = async username => {
  const { data } = await axios.post(`${baseUrl()}/users/check-username`, {
    username
  });
  return isTruthyFlag(data?.status);
};

/* ------------------------------------------------------------------------ */
/*                        Admin — approval queue (1–4)                       */
/* ------------------------------------------------------------------------ */

/**
 * POST /users/registrations/list (R3.1)
 * { status: 'pending' | 'approved' | 'rejected' | 'all', page, limit }
 * → { rows, total }
 */
export const listRegistrations = async ({
  status = 'pending',
  page = 1,
  limit = 25
} = {}) => {
  const { data } = await axios.post(
    `${baseUrl()}/users/registrations/list`,
    { status, page, limit },
    { headers: authHeaders() }
  );
  ensureOk(data, 'Unable to load registration requests');
  const rows = firstArray(
    data?.data,
    data?.registrations,
    data?.rows,
    data?.data?.rows,
    data
  );
  const rawTotal = [
    data?.total,
    data?.count,
    data?.totalCount,
    data?.total_count,
    data?.totalRecords,
    data?.total_records,
    data?.recordsTotal,
    data?.data?.total,
    data?.data?.count,
    data?.pagination?.total,
    data?.meta?.total
  ].find(
    value =>
      value !== undefined &&
      value !== null &&
      value !== '' &&
      Number.isFinite(Number(value))
  );
  const hasTotal = rawTotal !== undefined;
  // No total in the response: all we know is how many rows came back.
  const total = hasTotal ? Number(rawTotal) : rows.length;
  return { rows, total, hasTotal };
};

// Upper bound for counting rows when the server doesn't report a total.
const COUNT_FALLBACK_LIMIT = 1000;

/**
 * Total for one status tab. Cheap (limit 1) when the server reports a total;
 * otherwise the 1-row page would always "count" as 1, so fetch a large page
 * and count its rows instead.
 */
export const fetchRegistrationCount = async status => {
  const first = await listRegistrations({ status, page: 1, limit: 1 });
  if (first.hasTotal) {
    return first.total;
  }
  if (first.rows.length === 0) {
    return 0;
  }
  const { rows } = await listRegistrations({
    status,
    page: 1,
    limit: COUNT_FALLBACK_LIMIT
  });
  return rows.length;
};

/**
 * GET /users/registrations/:id (R3.2) — full submission incl. IP + address.
 */
export const fetchRegistration = async id => {
  const { data } = await axios.get(
    `${baseUrl()}/users/registrations/${encodeURIComponent(id)}`,
    { headers: authHeaders() }
  );
  ensureOk(data, 'Unable to load this request');
  if (Array.isArray(data?.data)) {
    return data.data[0] || {};
  }
  const record = [data?.data, data?.registration, data].find(
    candidate =>
      isPlainObject(candidate) &&
      (candidate.id !== undefined || candidate.email !== undefined)
  );
  return record || {};
};

/**
 * POST /users/registrations/approve (R4.1)
 * → { status: true, user_id, email_sent }   (email_sent may be FALSE — R4.5)
 * → { status: false, message: 'Email already exists' }
 * → { status: false, message: 'You are not authorized to create a user with a
 *      higher privilege level than your own' }
 */
export const approveRegistration = payload =>
  runAction(
    () =>
      axios.post(`${baseUrl()}/users/registrations/approve`, payload, {
        headers: authHeaders()
      }),
    'Unable to approve this request.'
  );

/**
 * POST /users/registrations/reject (R3.4)
 * { registration_id, rejection_reason }
 */
export const rejectRegistration = ({ registrationId, reason }) =>
  runAction(
    () =>
      axios.post(
        `${baseUrl()}/users/registrations/reject`,
        { id: registrationId, rejection_reason: reason },
        { headers: authHeaders() }
      ),
    'Unable to reject this request.'
  );

/**
 * POST /users/check-email — same check AddFacilityStaff runs before creating
 * a user. Resolves true when no account uses the email yet.
 */
export const checkEmailAvailability = async email => {
  const { data } = await axios.post(
    `${baseUrl()}/users/check-email`,
    { email },
    { headers: authHeaders() }
  );
  return isTruthyFlag(data?.status);
};

/**
 * GET /day-programs/list — the day-program picker source AddFacilityStaff
 * uses for Day Program Users (role 6) and Facility Staff (role 5).
 */
export const fetchDayPrograms = async () => {
  const { data } = await axios.get(`${baseUrl()}/day-programs/list`, {
    headers: authHeaders()
  });
  return firstArray(
    data?.data?.day_programs,
    data?.day_programs,
    data?.data,
    data
  );
};

/* ------------------------------------------------------------------------ */
/*        Admin (roles 1–3) — email domains that identify a group           */
/* ------------------------------------------------------------------------ */

const normalizeDomainRow = row => ({
  ...row,
  id: row?.id ?? row?.domain_id,
  group_id: row?.group_id,
  group_name: String(row?.group_name ?? '').trim(),
  domain: String(row?.domain ?? '')
    .trim()
    .toLowerCase(),
  is_active:
    row?.is_active === undefined || row?.is_active === null
      ? true
      : isTruthyFlag(row.is_active),
  claim_count: Number(row?.claim_count) || 0
});

/**
 * GET /facilities/allowed-domains
 * → { status, message, data: [{ id, group_id, group_name, domain, is_active,
 *     created_by, created, claim_count }] }
 * An empty list may come back as `status: false` ("not found"); that's
 * treated as no domains, not as an error.
 */
export const listAllowedDomains = async () => {
  const { data } = await axios.get(`${baseUrl()}/facilities/allowed-domains`, {
    headers: authHeaders()
  });
  if (
    isPlainObject(data) &&
    'status' in data &&
    !isTruthyFlag(data.status) &&
    (Array.isArray(data.data) ||
      /not found|no .*found/i.test(String(data.message || '')))
  ) {
    return [];
  }
  ensureOk(data, 'Unable to load email domains');
  return firstArray(data?.data, data?.domains, data)
    .map(normalizeDomainRow)
    .filter(row => row.id !== undefined && row.id !== null && row.domain);
};

/** POST /facilities/add-allowed-domain { group_id, domain } */
export const addAllowedDomain = ({ groupId, domain }) =>
  runAction(
    () =>
      axios.post(
        `${baseUrl()}/facilities/add-allowed-domain`,
        { group_id: groupId, domain },
        { headers: authHeaders() }
      ),
    'Unable to add this domain.'
  );

/**
 * POST /facilities/remove-allowed-domain { id }
 * Deactivates the domain: the row stays on the list with is_active = 0.
 */
export const removeAllowedDomain = id =>
  runAction(
    () =>
      axios.post(
        `${baseUrl()}/facilities/remove-allowed-domain`,
        { id },
        { headers: authHeaders() }
      ),
    'Unable to deactivate this domain.'
  );

/**
 * Turns an inactive domain back on. There's no status endpoint yet, so this
 * links the domain to its group again with POST /facilities/add-allowed-domain
 * — the same call as adding it by hand. Swap in a dedicated endpoint (e.g.
 * { id, is_active }) once the backend has one.
 */
export const reactivateAllowedDomain = ({ groupId, domain }) =>
  runAction(
    () =>
      axios.post(
        `${baseUrl()}/facilities/add-allowed-domain`,
        { group_id: groupId, domain },
        { headers: authHeaders() }
      ),
    'Unable to activate this domain.'
  );
